/**
=========================================================
* Soft UI Dashboard PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-material-ui
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// Core
import PropTypes from "prop-types";

// Components
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Box from "components/Atoms/Box";
import TypographyV1 from "components/Atoms/Typography/TypographyV1";
import AuthFooter from "components/Footers/AuthFooter";
import PageLayout from "components/LayoutContainers/PageLayout";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { selectThemeMode, setThemeMode } from "state/application/applicationSlice";

// Images
import cerBackground from "assets/images/cer2.svg";

// Styles
import styles from "components/LayoutContainers/CoverLayout/styles";

function CoverLayout({ header, title, description, image, top, children }) {
  const classes = styles({ image });
  const dispatch = useDispatch();
  const themeMode = useSelector(selectThemeMode);

  const handleThemeToggle = () => {
    const newMode = themeMode === "light" ? "dark" : "light";
    dispatch(setThemeMode(newMode));
  };

  const isDark = themeMode === "dark";

  return (
    <PageLayout background={isDark ? "#1a202c" : "white"}>
      {/* Theme Toggle Button */}
      <Box
        sx={{
          position: "fixed",
          top: 20,
          right: 20,
          zIndex: 10,
        }}
      >
        <IconButton
          onClick={handleThemeToggle}
          sx={{
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
            color: isDark ? "#ffffff" : "#2d3748",
            "&:hover": {
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.1)",
            },
          }}
        >
          <Icon fontSize="small">{isDark ? "light_mode" : "dark_mode"}</Icon>
        </IconButton>
      </Box>

      {/* Background Image */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${cerBackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          opacity: isDark ? 0.05 : 0.15,
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <Grid
        container
        justifyContent="center"
        className={classes.coverLayout}
        sx={{ position: "relative", zIndex: 1 }}
      >
        <Grid item xs={11} sm={8} md={5} xl={3}>
          <Box mt={top}>
            <Box
              sx={{
                background: isDark ? "#2d3748" : "#ffffff",
                borderRadius: "12px",
                border: "1px solid",
                borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)",
                boxShadow: isDark
                  ? "0 4px 24px rgba(0, 0, 0, 0.4)"
                  : "0 4px 24px rgba(0, 0, 0, 0.08)",
              }}
            >
              <Box pt={3} px={3} pb={1}>
                {!header ? (
                  <>
                    <Box mb={1}>
                      <TypographyV1
                        variant="h3"
                        fontWeight="bold"
                        sx={{
                          color: "#10b981",
                          background: "linear-gradient(195deg, #10b981, #059669)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          backgroundClip: "text",
                        }}
                      >
                        {title}
                      </TypographyV1>
                    </Box>
                    <TypographyV1 variant="body2" fontWeight="regular" textColor="text">
                      {description}
                    </TypographyV1>
                  </>
                ) : (
                  header
                )}
              </Box>
              <Box pt={1} px={3} pb={3}>
                {children}
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <AuthFooter />
    </PageLayout>
  );
}

// Setting default values for the props of CoverLayout
CoverLayout.defaultProps = {
  header: "",
  title: "",
  description: "",
  top: 8,
};

// Typechecking props for the CoverLayout
CoverLayout.propTypes = {
  header: PropTypes.node,
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string.isRequired,
  top: PropTypes.number,
  children: PropTypes.node.isRequired,
};

export default CoverLayout;
